import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import ModalPortal from '../../../../components/modal-portal';
import { Modal } from 'ui-components/components/modal';
import { Checkbox } from 'ui-components/components/checkbox';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { Button } from 'ui-components/components/button';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import {
  NotificationService,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TYPE_INFO,
} from 'ui-components/services/notification-service';

import { ProviderInviteStatusesLowerCase } from '../types';
import { getInviteStatus } from '../../api/vendor-api';
import { accept3PLInvite } from '../../api/fulfillment-delegation';

import messages from './messages';
import style from './style.module.less';
import { useOnComponentMount } from '../../../../hooks/use-on-component-mount';

const AcceptInviteModal = ({ openAutomationRulesModal, onClose }) => {
  const [isLoading, setIsLoading] = useAsyncSafeState(true);
  const [token, setToken] = useAsyncSafeState('');

  const [marketplaceName, setMarketplaceName] = useAsyncSafeState('');
  const [vendorName, setVendorName] = useAsyncSafeState('');
  const [vendorEmail, setVendorEmail] = useAsyncSafeState('');

  const [isValid, setIsValid] = useAsyncSafeState(false);
  const [didError, setDidError] = useAsyncSafeState(false);

  const [hasAcceptedTerms, setHasAcceptedTerms] = useAsyncSafeState(false);

  const intl = useIntl();

  const updateNotification = (notificationId, notificationType, title, content) => {
    NotificationService.updateNotification(notificationId, {
      title,
      content,
      notificationType,
      progress: false,
      dontHide: false,
    });
  };

  const cancelInviteModal = () => {
    onClose();
    NotificationService.showNotification({
      title: <FormattedMessage {...messages.connectionCancelledTitle} />,
      content: <FormattedMessage {...messages.connectionCancelledMessage} />,
      notificationType: NOTIFICATION_TYPE_ERROR,
      progress: false,
      dontHide: true,
    });
  };

  const acceptInvite = useCallback(async () => {
    onClose();
    const notificationId = NotificationService.showNotification({
      title: <FormattedMessage {...messages.establishingConnectionTitle} />,
      content: <FormattedMessage {...messages.establishingConnectionMessage} values={{ marketplaceName }} />,
      notificationType: NOTIFICATION_TYPE_INFO,
      progress: true,
      dontHide: true,
    });
    try {
      await accept3PLInvite(token, vendorName);
      updateNotification(
        notificationId,
        NOTIFICATION_TYPE_SUCCESS,
        <FormattedMessage {...messages.succesfulConnectionTitle} />,
        <FormattedMessage {...messages.succesfulConnectionMessage} values={{ marketplaceName }} />,
      );
      openAutomationRulesModal(marketplaceName);
    } catch {
      updateNotification(
        notificationId,
        NOTIFICATION_TYPE_ERROR,
        <FormattedMessage {...messages.failedConnectionTitle} />,
        <FormattedMessage {...messages.failedConnectionMessage} values={{ marketplaceName }} />,
      );
    }
  }, [marketplaceName, onClose, openAutomationRulesModal, token, vendorName]);

  const toggleAccept = () => {
    setHasAcceptedTerms((_hasAcceptedTerms) => !_hasAcceptedTerms);
  };

  useOnComponentMount(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setToken(urlParams.get('token'));
    getInviteStatus(urlParams.get('token'))
      .then((response) => {
        setDidError(false);
        if (response?.status.toLowerCase() === ProviderInviteStatusesLowerCase.pending) {
          setIsValid(true);
        } else {
          setIsValid(false);
        }
        setMarketplaceName(response?.sender_name);
        setVendorEmail(response?.recipient_email);
        setVendorName(response?.recipient_name);
        setIsLoading(false);
      })
      .catch(() => {
        setDidError(true);
        setIsValid(false);
        setIsLoading(false);
      });
  });

  if (isLoading) {
    return null;
  }

  if (!isValid) {
    return (
      <ModalPortal onClose={onClose}>
        <Modal size='small'>
          <ModalHeader>
            {didError ? <FormattedMessage {...messages.modalHeaderError} /> : <FormattedMessage {...messages.modalHeaderNotActive} />}
          </ModalHeader>
          <ModalBody className={style['ss-settings-modal-body']}>
            <div className={style['inactive-modal-body-text']}>
              {didError ? (
                <FormattedMessage {...messages.modalErrorMessage} />
              ) : (
                <FormattedMessage {...messages.inviteNotActiveBody} values={{ marketplaceName: marketplaceName || 'marketplace' }} />
              )}
            </div>
          </ModalBody>
          <ModalFooter className={style['ss-settings-modal-footer']}>
            <ModalFooterCancelButton>
              <FormattedMessage {...messages.closeButton} />
            </ModalFooterCancelButton>
          </ModalFooter>
        </Modal>
      </ModalPortal>
    );
  }

  return (
    <ModalPortal onCancel={cancelInviteModal} onClose={onClose}>
      <Modal size='medium'>
        <ModalHeader>
          <FormattedMessage {...messages.modalHeader} />
        </ModalHeader>
        <ModalBody className={style['ss-settings-modal-body']}>
          <div className={style['modal-body-text']}>
            <FormattedMessage
              {...messages.connectToMarketPlaceBody}
              values={{
                marketplaceName: marketplaceName || intl.formatMessage(messages.defaultMarketplace),
                vendorEmail,
              }}
            />
          </div>
          <div className={style['modal-body-confirm-agreement']}>
            <label className={style['checkbox-label']}>
              <FormattedMessage {...messages.confirmCheckboxLabel} />
            </label>
            <div className={style['checkbox-container']}>
              <Checkbox checked={hasAcceptedTerms} label={<FormattedMessage {...messages.confirmCheckboxText} />} onChange={toggleAccept} />
            </div>
          </div>
        </ModalBody>
        <ModalFooter className={style['ss-settings-modal-footer']}>
          <ModalFooterCancelButton>
            <FormattedMessage {...messages.cancelButton} />
          </ModalFooterCancelButton>
          <Button disabled={!hasAcceptedTerms} onClick={acceptInvite} theme='primary'>
            <FormattedMessage {...messages.connectButton} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};

AcceptInviteModal.propTypes = {
  openAutomationRulesModal: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AcceptInviteModal;
